import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Styles from "./styles.module.scss";
import { getReferrals, resetReferrals } from "../../store/cabinet/action";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../cabinetShell/config";

import avatarPlaceholder from "../../theme/assets/redesign/leaderboard/avatar_placeholder.webp";

const CabinetReferralTable = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const referrals = useSelector((store) => store.cabinet.referrals);
    const referralsMain = useSelector((store) => store.cabinet.referralsMain);

    // Скролл-контейнер и «сентинел»
    const scrollRootRef = useRef(null);
    const loadMoreRef = useRef(null);

    // activeLap: null = ещё не решено (ждём laps), false = «Все», number = выбранный lap id
    const [activeLap, setActiveLap] = useState(null);

    // Выбираем стартовый круг, когда laps доступны (берём id, не order)
    useEffect(() => {
        if (activeLap !== null) return; // уже решили, какой круг использовать
        if (!Array.isArray(referralsMain?.laps)) return; // laps ещё не пришли

        const initialLapId =
            referralsMain.laps.find((l) => l.status === "active")?.id ?? false;
        setActiveLap(initialLapId);
    }, [referralsMain?.laps, activeLap]);

    // Обработчик выбора круга — принимает id (или ничего для «Все»)
    const lapsHandler = (id) => {
        const nextLap = id || false; // false = «Все»
        setActiveLap(nextLap);
        // Снимем защиту от двойного старта (ниже) — чтобы перезагрузить под новый круг
        initialFetchGuardRef.current = null;
    };

    // Защита от двойного вызова эффекта в StrictMode (dev)
    // Запоминаем, для какого activeLap уже делали «первую» загрузку
    const initialFetchGuardRef = useRef(null);

    // Единственное место, где инициируем загрузку 1-й страницы
    useEffect(() => {
        if (activeLap === null) return; // не знаем круг — не грузим, чтобы не было лишних запросов

        // если уже грузили для этого lap — выходим (StrictMode вызывает эффект дважды)
        if (initialFetchGuardRef.current === activeLap) return;
        initialFetchGuardRef.current = activeLap;

        // Чистим текущие данные, чтобы не смешивать разные круги
        dispatch(resetReferrals());

        // Тянем первую страницу (без lap_id, если «Все»)
        dispatch(getReferrals(1, activeLap === false ? undefined : activeLap));
    }, [activeLap, dispatch]);

    // Инфинит-скролл в пределах contentBoxTable
    useEffect(() => {
        const rootEl = scrollRootRef.current;
        const targetEl = loadMoreRef.current;
        if (!rootEl || !targetEl) return;

        const current = referrals?.referrals?.current_page ?? 1;
        const last = referrals?.referrals?.last_page ?? 1;
        if (current >= last) return; // страниц больше нет — не наблюдаем

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry?.isIntersecting) return;

                // Временно отписываемся, чтобы исключить дубли
                observer.unobserve(targetEl);

                // Догружаем следующую страницу с текущим фильтром по lap id
                dispatch(getReferrals(current + 1, activeLap === false ? undefined : activeLap));
            },
            {
                root: rootEl,               // наблюдаем относительно скролл-контейнера
                rootMargin: "200px 0px 0px",
                threshold: 0,
            }
        );

        observer.observe(targetEl);
        return () => observer.disconnect();
    }, [
        dispatch,
        referrals?.referrals?.current_page,
        referrals?.referrals?.last_page,
        activeLap, // при смене круга перевешиваем наблюдатель
    ]);

    const laps = Array.isArray(referralsMain?.laps) ? referralsMain.laps : [];

    return (
        <section className={Styles.container}>
            <div className={Styles.top}>
                <div className={Styles.heading}>
                    <div className={Styles.title}>{t("Статистика рефералов")}</div>
                    <div className={Styles.subtitle}>{t("Ваши приглашённые и бонусы по ним")}</div>
                </div>
                {/* фильтр по кругам: «Все» + круги из referralsMain.laps */}
                <div className={Styles.filters}>
                    <button
                        type="button"
                        className={`${Styles.chip} ${activeLap === false ? Styles.chipActive : ""}`}
                        onClick={() => lapsHandler()}
                    >
                        {t("Все")}
                    </button>
                    {laps.map((item) => (
                        <button
                            type="button"
                            key={`lap-${item.id}`}
                            className={`${Styles.chip} ${activeLap === item.id ? Styles.chipActive : ""}`}
                            onClick={() => lapsHandler(item.id)}
                        >
                            {t("Круг")} #{item.order}
                        </button>
                    ))}
                </div>
            </div>

            <div className={Styles.header}>
                <div className={Styles.colLevel}>{t("Уровень")}</div>
                <div className={`${Styles.colAvatar} ${Styles.desktopOnly}`}/>
                <div className={Styles.colNickname}>{t("Никнейм")}</div>
                <div className={Styles.colTurnover}>{t("Оборот")}</div>
                <div className={Styles.colBonus}>{t("Ваш бонус")}</div>
            </div>

            {/* скролл-контейнер для инфинит-скролла */}
            <div className={Styles.body} ref={scrollRootRef}>
                {Array.isArray(referrals?.data) && referrals.data.length > 0 ? (
                    referrals.data.map((item, index) => (
                        <div className={Styles.row} key={`referralsRow${item?.user?.id ?? index}`}>
                            <div className={Styles.colLevel}><span>{item?.level ?? "—"}</span></div>
                            <div className={Styles.colAvatar}>
                                <img src={item?.user?.avatar || avatarPlaceholder} alt=""/>
                            </div>
                            <div className={Styles.colNickname}>
                                <span>{item?.user?.username ? `@${item.user.username}` : "—"}</span>
                            </div>
                            <div className={Styles.colTurnover}>{formatNumber(item?.user?.tokens)} FTF</div>
                            <div className={Styles.colBonus}>{formatNumber(item?.user?.bought_tokens_total)} FTF</div>
                        </div>
                    ))
                ) : (
                    activeLap !== null ? <div className={Styles.empty}>{t("Пока нет приглашённых")}</div> : null
                )}

                {/* Сентинел для IntersectionObserver */}
                <div ref={loadMoreRef} style={{ height: "5px", flexShrink: 0 }} aria-hidden="true" />
            </div>
        </section>
    );
};

export default CabinetReferralTable;
